import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const WhoopRecoveryCard = () => {
  const [recoveryData, setRecoveryData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [loading, setLoading] = useState(true);

  const whoopUserId = localStorage.getItem('whoop_user_id');

  const loadData = useCallback(async () => {
    if (!whoopUserId) return;
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const sevenDaysAgo = new Date(Date.now() - 7 * 86400000)
        .toISOString().split('T')[0];

      const [recoveryRes, sleepRes] = await Promise.all([
        fetch(`${API_BASE}/api/whoop/oauth/stored/recovery?user_id=${whoopUserId}&start_date=${sevenDaysAgo}&end_date=${today}`),
        fetch(`${API_BASE}/api/whoop/oauth/stored/sleep?user_id=${whoopUserId}&start_date=${sevenDaysAgo}&end_date=${today}`)
      ]);

      if (recoveryRes.ok) {
        const data = await recoveryRes.json();
        setRecoveryData(
          (data.records || []).sort((a, b) => b.date.localeCompare(a.date))
        );
      }
      if (sleepRes.ok) {
        const data = await sleepRes.json();
        setSleepData(
          (data.records || []).sort((a, b) => b.date.localeCompare(a.date))
        );
      }
    } catch (err) {
      console.error('Failed to load Whoop data:', err);
    } finally {
      setLoading(false);
    }
  }, [whoopUserId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!whoopUserId) return null;
  if (!loading && recoveryData.length === 0 && sleepData.length === 0) return null;

  const todayRecovery = recoveryData[0] || null;
  const todaySleep = sleepData[0] || null;

  const getScoreColor = (score) => {
    if (score == null) return 'var(--text-light)';
    if (score >= 67) return '#22c55e';
    if (score >= 34) return '#f59e0b';
    return '#ef4444';
  };

  const formatSleepTime = (ms) => {
    if (!ms) return '--';
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Today';
    const date = new Date(dateStr + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date.getTime() === today.getTime()) return 'Today';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Sparkline: oldest to newest (left to right)
  const sparklineData = [...recoveryData]
    .reverse()
    .map(r => r.recovery_score)
    .filter(v => v != null);

  const renderSparkline = (data) => {
    if (data.length < 2) return null;
    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const padding = 4;
    const w = 200;
    const h = 40;

    const points = data.map((val, i) => {
      const x = padding + (i / (data.length - 1)) * (w - 2 * padding);
      const y = h - padding - ((val - min) / range) * (h - 2 * padding);
      return `${x},${y}`;
    }).join(' ');

    const circles = data.map((val, i) => {
      const x = padding + (i / (data.length - 1)) * (w - 2 * padding);
      const y = h - padding - ((val - min) / range) * (h - 2 * padding);
      return <circle key={i} cx={x} cy={y} r="3" fill="var(--primary)" />;
    });

    return (
      <svg className="whoop-sparkline" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polyline
          points={points}
          fill="none"
          stroke="var(--primary)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {circles}
      </svg>
    );
  };

  return (
    <div className="whoop-recovery-card home-card">
      <div className="whoop-recovery-card__header">
        <h3 className="card-title">Whoop Recovery</h3>
        <span className="whoop-recovery-card__date">
          {formatDate(todayRecovery?.date)}
        </span>
      </div>

      {loading ? (
        <div className="whoop-recovery-card__loading">Loading recovery data...</div>
      ) : (
        <>
          <div className="whoop-recovery-card__body">
            <div
              className="whoop-recovery-card__score"
              style={{ color: getScoreColor(todayRecovery?.recovery_score) }}
            >
              <span className="score-value">
                {todayRecovery?.recovery_score != null
                  ? `${Math.round(todayRecovery.recovery_score)}%`
                  : '--'}
              </span>
              <span className="score-label">Recovery</span>
            </div>

            <div className="whoop-recovery-card__metrics">
              <div className="whoop-metric">
                <span className="whoop-metric__value">
                  {todayRecovery?.hrv_rmssd != null
                    ? `${Math.round(todayRecovery.hrv_rmssd)}`
                    : '--'}
                </span>
                <span className="whoop-metric__label">HRV (ms)</span>
              </div>
              <div className="whoop-metric">
                <span className="whoop-metric__value">
                  {todayRecovery?.resting_heart_rate != null
                    ? `${Math.round(todayRecovery.resting_heart_rate)}`
                    : '--'}
                </span>
                <span className="whoop-metric__label">Resting HR</span>
              </div>
              <div className="whoop-metric">
                <span className="whoop-metric__value">
                  {formatSleepTime(todaySleep?.total_sleep_time)}
                </span>
                <span className="whoop-metric__label">Sleep</span>
              </div>
              <div className="whoop-metric">
                <span className="whoop-metric__value">
                  {todaySleep?.sleep_efficiency != null
                    ? `${Math.round(todaySleep.sleep_efficiency)}%`
                    : '--'}
                </span>
                <span className="whoop-metric__label">Efficiency</span>
              </div>
            </div>
          </div>

          {sparklineData.length > 1 && (
            <div className="whoop-recovery-card__trend">
              <span className="trend-label">7-Day Recovery</span>
              {renderSparkline(sparklineData)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WhoopRecoveryCard;
